import React, { useState, useEffect, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useAuthState } from "../../context/context";
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { enumCompanyStatus } from '../../enums/enumCompanyStatus';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import EntrepriseService from '../../services/entrepriseService';
import { DT_entrepriseRerecuteurs } from './dt_entrepriseRecruteurs';
import './dt_entreprises.css';

export const DT_Entreprise = () => {

    const history = useHistory();
    const { t } = useTranslation();
    const [entreprises, setEntreprises] = useState([]);
    const [selectedEntreprises, setSelectedEntreprises] = useState(null);
    const [confirmationEmail, setConfirmationEmail] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [totalRecords, setTotalCount] = useState(0);
    const toast = useRef(null);
    const dt = useRef(null);
    const entrepriseService = new EntrepriseService();
    const currentUser = useAuthState();
    const [loading, setLoading] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: 'creationDate',
        sortOrder: 0
    });
    const isMounted = useRef(false);

    const [entrepriseToDelete, setEntrepriseToDelete] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        isMounted.current = true;
        loadLazyData();
    }, [lazyParams]);


    const loadLazyData = () => {
        setLoading(true);
        
        // réinitialisation de l'expansion
        setExpandedRows({});

        entrepriseService.getEntreprisesPaginated(currentUser, lazyParams).then(_entreprises => {
            _entreprises.items.forEach((e) => {
                e.id = e.id ? e.id : null;
                e.abonnementEntreprise = e.abonnementEntreprise ? e.abonnementEntreprise.label : null;
                e.creationDate = e.creationDate ? new Date(e.creationDate).toLocaleString() : null;
            });
            setEntreprises(_entreprises.items);
            setTotalCount(_entreprises.totalCount);
        })
        .catch(err => showError())
        .finally(() => setLoading(false))
    }

    const showError = () => {
        toast.current.show({ severity: 'error', summary: t('general.failed'), detail: t('general.loading_failed'), life: 3000 });
    }

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    }

    const onFilter = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams['page'] = 0;
        _lazyParams['first'] = 0;
        setLazyParams(_lazyParams);
    }

    const onPage = (event) => {
        // gestion de la pagination
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    }

    const rowExpansionTemplate = (data) => (
        <DT_entrepriseRerecuteurs entrepriseId={data.id} />
    )

    const lienAbonnementEntreprise = (recruteur) => {
        // à modifier pour utiiliser l'id entreprise et non l'id recruteur
        history.push({pathname:'/subscription_admin_company',state:{adminImpersonatingRecruteurId:recruteur.id}});
    }

    const handleClickConfirmDelete = (entreprise) => {
        setEntrepriseToDelete(entreprise);
        setConfirmationEmail('');
        setConfirmVisible(true);
    };

    const deleteEntreprise = (entreprise) => {
        if (confirmationEmail != entreprise.nom) {
            toast.current.show({ severity: 'error', summary: t('general.failed'), detail: t('company.confirm_delete_entreprise_error'), life: 3000 });
            return;
        }
        entrepriseService.deleteEntreprise(entreprise.id).then(() => {
            toast.current.show({ severity: 'success', summary: t('general.success'), detail: t('company.confirm_delete_entreprise_success'), life: 3000 });
            loadLazyData();
        })
        .catch(err => {
            toast.current.show({ severity: 'error', summary: t('general.failed'), detail: t('company.confirm_delete_entreprise_error'), life: 3000 });
        });
        setConfirmVisible(false);
        setConfirmationEmail('');
    }

    const actionBodyTemplateEntreprisesPaginated = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-dollar" className="p-button-rounded p-button-warning p-mr-2" onClick={() => lienAbonnementEntreprise(rowData)} disabled={true} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => handleClickConfirmDelete(rowData)} disabled={true} />
            </React.Fragment>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="title"><Trans i18nKey="company.companies_management.full" /></h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t('general.search')} />
            </span>
        </div>
    );

    return (
        <div className="datatable-entreprises">
            <Toast ref={toast} />
            <ConfirmDialog
                visible={confirmVisible}
                onHide={() => setConfirmVisible(false)}
                message={
                    <div>
                        <Trans
                            i18nKey="company.confirm_delete_message_entreprise"
                            values={{ nomEntreprise: entrepriseToDelete?.nom }}
                            components={{ br: <br />, b: <b /> }}
                        />
                        <InputText
                            className="dip-w-100"
                            placeholder={entrepriseToDelete?.nom}
                            value={confirmationEmail}
                            onChange={(e) => setConfirmationEmail(e.target.value)}
                        />
                    </div>
                }
                header={t('general.confirmation')}
                icon="pi pi-exclamation-triangle"
                acceptLabel={t('general.delete')}
                rejectLabel={t('general.cancel')}
                accept={() => {
                    deleteEntreprise(entrepriseToDelete);
                }}
                reject={() => {
                    setConfirmVisible(false);
                    setConfirmationEmail('');
                }}
            />
            <div className="card">
                <DataTable 
                    ref={dt} 
                    value={entreprises} 
                    lazy         
                    filterDelay={1000}
                    paginator 
                    first={lazyParams.first}
                    rows={lazyParams.rows} 
                    emptyMessage={t('company.no_company_to_show')} 
                    selection={selectedEntreprises} 
                    onSelectionChange={(e) => setSelectedEntreprises(e.value)}
                    dataKey="id" 
                    rowsPerPageOptions={[5, 10, 25]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={t('company.footer_count_pagin')}
                    globalFilter={globalFilter}
                    header={header}
                    loading={loading}
                    columnResizeMode="expand"
                    resizableColumns 
                    responsiveLayout="auto"
                    autoLayout
                    onSort={onSort} 
                    sortField={lazyParams.sortField} 
                    sortOrder={lazyParams.sortOrder}
                    onPage={onPage}
                    onFilter={onFilter}
                    filters={lazyParams.filters}
                    totalRecords={totalRecords}


                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                >
                    <Column expander style={{ width: '3em' }} />
                    <Column field="nom" header={t('general.company')} sortable></Column>
                    <Column field="abonnementEntreprise" header={t('subscription.current_subscriptions')} sortable></Column>
                    <Column field="creationDate" header={t('date.creation')} sortable></Column>
                    <Column body={actionBodyTemplateEntreprisesPaginated}></Column>
                </DataTable>
            </div>
        </div>
    );
}